import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import {
  deleteBrowserSession, establishBrowserSession, refreshBrowserSession, SessionExpiredError,
} from './staff-session';
import type { RuntimeConfig } from './types';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated' | 'error';

export class AuthState {
  status = $state<AuthStatus>('loading');
  user = $state<User | null>(null);
  message = $state('');
  private manager: UserManager | null = null;

  async initialise(config: RuntimeConfig): Promise<void> {
    const authority = `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`;
    this.manager = new UserManager({
      authority,
      client_id: config.userPoolClientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/`,
      response_type: 'code',
      scope: config.scopes.join(' '),
      loadUserInfo: false,
      monitorSession: false,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });
    try {
      let user = await this.manager.getUser();
      if (user?.refresh_token) user = await this.establishSession(user);
      if (!user || user.expired) user = await this.restoreSession();
      if (!user) {
        this.user = null;
        this.status = 'anonymous';
        return;
      }
      this.user = user;
      this.status = 'authenticated';
    } catch {
      this.status = 'error';
      this.message = 'Your sign-in session could not be loaded.';
    }
  }

  async signIn(): Promise<void> {
    await this.manager?.signinRedirect();
  }

  async completeCallback(): Promise<void> {
    if (!this.manager) throw new Error('Authentication is not initialised');
    const callbackUser = await this.manager.signinRedirectCallback();
    try {
      this.user = await this.establishSession(callbackUser);
    } catch (error) {
      await this.manager.removeUser();
      throw error;
    }
    this.status = 'authenticated';
  }

  async signOut(): Promise<void> {
    try {
      await deleteBrowserSession();
    } finally {
      await this.manager?.signoutRedirect();
    }
  }

  async accessToken(): Promise<string> {
    if (!this.manager || !this.user) throw new Error('No active session');
    if (this.user.expired) this.user = await this.restoreSession();
    const currentUser = this.user;
    if (!currentUser) throw new Error('Your session has expired');
    return currentUser.access_token;
  }

  hasScope(scope: string): boolean {
    return this.user?.scopes.includes(scope) ?? false;
  }

  async expire(): Promise<void> {
    try {
      await deleteBrowserSession();
    } catch {
      // Local expiry must still complete when the session service is unavailable.
    }
    await this.manager?.removeUser();
    this.user = null;
    this.status = 'anonymous';
  }

  private async establishSession(user: User): Promise<User> {
    if (!this.manager) throw new Error('Authentication is not initialised');
    const established = await establishBrowserSession(user);
    await this.manager.storeUser(established);
    return established;
  }

  private async restoreSession(): Promise<User | null> {
    try {
      const user = await refreshBrowserSession();
      await this.manager?.storeUser(user);
      return user;
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) throw error;
    }
    await this.manager?.removeUser();
    this.user = null;
    this.status = 'anonymous';
    return null;
  }
}
